import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import LoadingDataDiv from "../common/LoadingDataDiv";
import EmployeeFolderDocumentsCard from "../common/EmployeeFolderDocumentsCard";
import { toastError, toastSuccess } from "../../../helpers/Toast";
import "../../../css/EmployeePortal.css";

const todayString = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const datePlusDaysString = days => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const formatDate = value => value ? new Date(`${value}T12:00:00`).toLocaleDateString("pl-PL") : "-";
const formatDateTime = value => value ? new Date(value).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }) : "-";
const formatBytes = value => {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const errorMessage = error => error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null) || "Operacja nie powiodła się.";

const requestStatusLabels = {
  AWAITING_TARGET: "Czeka na drugiego pracownika",
  PENDING: "Oczekuje na kierownika",
  APPROVED: "Zatwierdzony",
  REJECTED: "Odrzucony",
  CANCELLED: "Anulowany"
};

const availabilityTypeLabels = {
  AVAILABLE: "Mogę pracować",
  PREFERRED: "Preferuję pracę",
  UNAVAILABLE: "Niedostępny"
};

const contactTypeLabels = {
  HR_MEETING: "Spotkanie z HR",
  QUESTION_TO_MANAGER: "Pytanie do kierownika",
  ANONYMOUS_REPORT: "Anonimowe zgłoszenie"
};

const contactStatusLabels = {
  NEW: "Nowe",
  IN_PROGRESS: "W trakcie",
  CLOSED: "Zamknięte"
};

const appointmentStatusLabels = {
  BOOKED: "Zarezerwowane",
  COMPLETED: "Odbyte",
  CANCELLED_BY_EMPLOYEE: "Anulowane przez Ciebie",
  CANCELLED_BY_HR: "Anulowane przez HR",
  NO_SHOW: "Nieobecność"
};

const groupMeetingStatusLabels = {
  SCHEDULED: "Zaplanowane",
  COMPLETED: "Zakończone",
  CANCELLED: "Anulowane"
};

const groupMeetingRsvpLabels = {
  NO_RESPONSE: "Brak odpowiedzi",
  YES: "Będę",
  MAYBE: "Wstępnie tak",
  NO: "Na pewno nie"
};

const resourceCategoryLabels = {
  REGULATION: "Regulamin",
  DOCUMENT_TEMPLATE: "Wzór dokumentu",
  TRAINING_MATERIAL: "Materiał szkoleniowy",
  OTHER: "Inne"
};

const weekdayOptions = [
  ["MONDAY", "Pn"],
  ["TUESDAY", "Wt"],
  ["WEDNESDAY", "Śr"],
  ["THURSDAY", "Cz"],
  ["FRIDAY", "Pt"],
  ["SATURDAY", "So"],
  ["SUNDAY", "Nd"]
];

const weekdayLabels = Object.fromEntries(weekdayOptions);
const formatWeekdays = days => (days || []).map(day => weekdayLabels[day] || day).join(", ");

const emptyDateRequest = () => ({
  type: "DAY_OFF",
  dateFrom: todayString(),
  dateTo: todayString(),
  absenceTypeId: "",
  employeeComment: ""
});

const emptyAvailabilityRequest = () => ({
  dateFrom: todayString(),
  dateTo: todayString(),
  availabilityType: "AVAILABLE",
  availabilityTimeFrom: "",
  availabilityTimeTo: "",
  availabilityRecurrence: "NONE",
  availabilityDaysOfWeek: [],
  employeeComment: ""
});

const emptyContactRequest = () => ({
  type: "QUESTION_TO_MANAGER",
  targetManagerEmployeeId: "",
  message: ""
});

const emptyAppointmentRequest = () => ({
  subject: "",
  message: ""
});

const EmployeePortal = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overview, setOverview] = useState(null);
  const [availabilities, setAvailabilities] = useState([]);
  const [requests, setRequests] = useState([]);
  const [openShiftCount, setOpenShiftCount] = useState(0);
  const [incomingSwapCount, setIncomingSwapCount] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [publishedResources, setPublishedResources] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [appointmentSlots, setAppointmentSlots] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [groupMeetingInvitations, setGroupMeetingInvitations] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);
  const [activeTest, setActiveTest] = useState(null);
  const [testAnswers, setTestAnswers] = useState({});
  const [showTestModal, setShowTestModal] = useState(false);
  const [submittingTest, setSubmittingTest] = useState(false);
  const [activeSection, setActiveSection] = useState("start");
  const [showDateRequest, setShowDateRequest] = useState(false);
  const [showAvailabilityRequest, setShowAvailabilityRequest] = useState(false);
  const [showContactRequest, setShowContactRequest] = useState(false);
  const [selectedAppointmentSlot, setSelectedAppointmentSlot] = useState(null);
  const [appointmentRequest, setAppointmentRequest] = useState(emptyAppointmentRequest());
  const [dateRequest, setDateRequest] = useState(emptyDateRequest());
  const [availabilityRequest, setAvailabilityRequest] = useState(emptyAvailabilityRequest());
  const [contactRequest, setContactRequest] = useState(emptyContactRequest());
  const [documentType, setDocumentType] = useState("STUDENT_ID");
  const [documentFile, setDocumentFile] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewResponse, availabilityResponse, requestResponse, documentResponse, resourceResponse, contactResponse, appointmentSlotsResponse, appointmentsResponse, groupMeetingsResponse, absenceTypeResponse, testsResponse] = await Promise.all([
        axiosPrivate.get("/employee-self-service/overview"),
        axiosPrivate.get("/employee-self-service/availabilities"),
        axiosPrivate.get("/schedule-change-requests/mine"),
        axiosPrivate.get("/employee-self-documents"),
        axiosPrivate.get("/employee-resources"),
        axiosPrivate.get("/employee-contact-requests/mine"),
        axiosPrivate.get("/hr-appointments/mine/slots"),
        axiosPrivate.get("/hr-appointments/mine"),
        axiosPrivate.get("/hr-appointments/mine/group-meetings"),
        axiosPrivate.get("/absence-types"),
        axiosPrivate.get("/employee-training-tests/available")
      ]);
      setOverview(overviewResponse.data);
      setAvailabilities(availabilityResponse.data || []);
      setRequests(requestResponse.data || []);
      setDocuments(documentResponse.data || []);
      setPublishedResources(resourceResponse.data || []);
      setContacts(contactResponse.data || []);
      setAppointmentSlots(appointmentSlotsResponse.data || []);
      setAppointments(appointmentsResponse.data || []);
      setGroupMeetingInvitations(groupMeetingsResponse.data || []);
      setAbsenceTypes(absenceTypeResponse.data || []);
      setAvailableTests(testsResponse.data || []);

      const workflowResponses = await Promise.allSettled([
        axiosPrivate.get("/schedule-change-requests/open-shifts", {
          params: { dateFrom: todayString(), dateTo: datePlusDaysString(30) }
        }),
        axiosPrivate.get("/schedule-change-requests/mine/incoming-swaps")
      ]);
      setOpenShiftCount(workflowResponses[0].status === "fulfilled" ? (workflowResponses[0].value.data || []).length : 0);
      setIncomingSwapCount(workflowResponses[1].status === "fulfilled" ? (workflowResponses[1].value.data || []).length : 0);
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [axiosPrivate]);

  const pendingCount = useMemo(() => requests.filter(request => ["PENDING", "AWAITING_TARGET"].includes(request.status)).length, [requests]);
  const availabilityRequests = useMemo(() => requests.filter(request => request.type === "AVAILABILITY"), [requests]);
  const absenceRequests = useMemo(() => requests.filter(request => ["DAY_OFF", "VACATION"].includes(request.type)), [requests]);
  const supervisorOptions = useMemo(() => {
    const unique = new Map();
    (overview?.supervisors || []).forEach(supervisor => {
      if (supervisor?.employeeId && !unique.has(supervisor.employeeId)) unique.set(supervisor.employeeId, supervisor);
    });
    return [...unique.values()];
  }, [overview]);

  const openContactRequest = () => {
    setContactRequest({
      ...emptyContactRequest(),
      targetManagerEmployeeId: supervisorOptions.length === 1 ? String(supervisorOptions[0].employeeId) : ""
    });
    setShowContactRequest(true);
  };

  const submitDateRequest = async () => {
    if (!dateRequest.dateFrom || !dateRequest.dateTo || dateRequest.dateTo < dateRequest.dateFrom) {
      toastError("Podaj prawidłowy okres wniosku.");
      return;
    }
    if (dateRequest.type === "VACATION" && !dateRequest.absenceTypeId) {
      toastError("Wybierz typ nieobecności.");
      return;
    }
    setSaving(true);
    try {
      await axiosPrivate.post("/schedule-change-requests/mine", {
        type: dateRequest.type,
        dateFrom: dateRequest.dateFrom,
        dateTo: dateRequest.dateTo,
        absenceTypeId: dateRequest.type === "VACATION" ? Number(dateRequest.absenceTypeId) : null,
        employeeComment: dateRequest.employeeComment
      });
      toastSuccess("Wniosek został wysłany do kierownika.");
      setShowDateRequest(false);
      setDateRequest(emptyDateRequest());
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleWeekday = day => {
    setAvailabilityRequest(prev => ({
      ...prev,
      availabilityDaysOfWeek: prev.availabilityDaysOfWeek.includes(day)
        ? prev.availabilityDaysOfWeek.filter(item => item !== day)
        : [...prev.availabilityDaysOfWeek, day]
    }));
  };

  const submitAvailabilityRequest = async () => {
    if (!availabilityRequest.dateFrom || !availabilityRequest.dateTo || availabilityRequest.dateTo < availabilityRequest.dateFrom) {
      toastError("Podaj prawidłowy okres dostępności.");
      return;
    }
    if (Boolean(availabilityRequest.availabilityTimeFrom) !== Boolean(availabilityRequest.availabilityTimeTo)) {
      toastError("Podaj obie godziny albo pozostaw obie puste.");
      return;
    }
    if (availabilityRequest.availabilityTimeFrom && availabilityRequest.availabilityTimeFrom >= availabilityRequest.availabilityTimeTo) {
      toastError("Godzina zakończenia musi być późniejsza niż rozpoczęcia.");
      return;
    }
    if (availabilityRequest.availabilityRecurrence === "WEEKLY" && availabilityRequest.availabilityDaysOfWeek.length === 0) {
      toastError("Wybierz co najmniej jeden dzień tygodnia.");
      return;
    }
    setSaving(true);
    try {
      await axiosPrivate.post("/schedule-change-requests/mine", {
        type: "AVAILABILITY",
        ...availabilityRequest,
        availabilityTimeFrom: availabilityRequest.availabilityTimeFrom || null,
        availabilityTimeTo: availabilityRequest.availabilityTimeTo || null
      });
      toastSuccess("Deklaracja dostępności została wysłana do kierownika.");
      setShowAvailabilityRequest(false);
      setAvailabilityRequest(emptyAvailabilityRequest());
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const openTrainingTest = async test => {
    try {
      const response = await axiosPrivate.get(`/employee-training-tests/${test.id}`);
      setActiveTest(response.data);
      setTestAnswers({});
      setShowTestModal(true);
    } catch (error) {
      toastError(errorMessage(error));
    }
  };

  const toggleTestAnswer = (question, answerId) => {
    setTestAnswers(prev => {
      const current = prev[question.id] || [];
      if (!question.multipleAnswers) return {...prev, [question.id]: [answerId]};
      return {
        ...prev,
        [question.id]: current.includes(answerId) ? current.filter(id => id !== answerId) : [...current, answerId]
      };
    });
  };

  const submitTrainingTest = async () => {
    if (!activeTest) return;
    const unanswered = (activeTest.questions || []).some(question => !(testAnswers[question.id] || []).length);
    if (unanswered) {
      toastError("Odpowiedz na wszystkie pytania przed wysłaniem testu.");
      return;
    }
    setSubmittingTest(true);
    try {
      const response = await axiosPrivate.post(`/employee-training-tests/${activeTest.id}/submit`, {answers: testAnswers});
      const result = response.data;
      toastSuccess(result.passed ? `Test zaliczony: ${result.score}%` : `Test niezaliczony: ${result.score}%`);
      setShowTestModal(false);
      setActiveTest(null);
      setTestAnswers({});
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSubmittingTest(false);
    }
  };

  const cancelRequest = async id => {
    setSaving(true);
    try {
      await axiosPrivate.delete(`/schedule-change-requests/mine/${id}`);
      toastSuccess("Wniosek został anulowany.");
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const uploadDocument = async event => {
    event.preventDefault();
    if (!documentFile) {
      toastError("Wybierz plik.");
      return;
    }
    const data = new FormData();
    data.append("type", documentType);
    data.append("file", documentFile);
    setSaving(true);
    try {
      await axiosPrivate.post("/employee-self-documents", data, { headers: { "Content-Type": "multipart/form-data" } });
      toastSuccess("Dokument został przesłany.");
      setDocumentFile(null);
      event.target.reset();
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const downloadDocument = async document => {
    try {
      const response = await axiosPrivate.get(`/employee-self-documents/${document.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = document.fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastError(errorMessage(error));
    }
  };

  const deleteDocument = async id => {
    setSaving(true);
    try {
      await axiosPrivate.delete(`/employee-self-documents/${id}`);
      toastSuccess("Dokument został usunięty.");
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const openPublishedResource = async resource => {
    if (resource.kind === "LINK") {
      window.open(resource.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const response = await axiosPrivate.get(`/employee-resources/${resource.id}/download`, {responseType: "blob"});
      const url = URL.createObjectURL(response.data);
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = resource.fileName || resource.title;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastError(errorMessage(error));
    }
  };

  const submitContactRequest = async () => {
    if (!contactRequest.message.trim()) {
      toastError("Wpisz treść zgłoszenia.");
      return;
    }
    if (contactRequest.type === "QUESTION_TO_MANAGER" && !contactRequest.targetManagerEmployeeId) {
      toastError(supervisorOptions.length ? "Wybierz kierownika." : "Nie masz przypisanego kierownika, do którego można wysłać pytanie.");
      return;
    }
    setSaving(true);
    try {
      await axiosPrivate.post("/employee-contact-requests/mine", {
        ...contactRequest,
        targetManagerEmployeeId: contactRequest.type === "QUESTION_TO_MANAGER" ? Number(contactRequest.targetManagerEmployeeId) : null
      });
      toastSuccess("Zgłoszenie zostało wysłane.");
      setShowContactRequest(false);
      setContactRequest(emptyContactRequest());
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };


  const bookAppointment = async () => {
    if (!selectedAppointmentSlot) return;
    if (!appointmentRequest.subject.trim()) {
      toastError("Wpisz temat spotkania.");
      return;
    }
    setSaving(true);
    try {
      await axiosPrivate.post("/hr-appointments/mine", {
        slotId: selectedAppointmentSlot.id,
        subject: appointmentRequest.subject.trim(),
        message: appointmentRequest.message.trim() || null
      });
      toastSuccess("Termin spotkania został zarezerwowany.");
      setSelectedAppointmentSlot(null);
      setAppointmentRequest(emptyAppointmentRequest());
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const cancelAppointment = async appointmentId => {
    if (!window.confirm("Anulować tę rezerwację spotkania?")) return;
    setSaving(true);
    try {
      await axiosPrivate.delete(`/hr-appointments/mine/${appointmentId}`);
      toastSuccess("Rezerwacja spotkania została anulowana.");
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const respondToGroupMeeting = async (invitation, status) => {
    setSaving(true);
    try {
      await axiosPrivate.put(`/hr-appointments/mine/group-meetings/${invitation.id}/rsvp`, {
        status,
        version: invitation.version
      });
      toastSuccess("Odpowiedź dotycząca spotkania została zapisana.");
      await loadData();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div id="layoutSidenav_content"><LoadingDataDiv /></div>;
  }

  if (!overview) {
    return <div id="layoutSidenav_content"><div className="container-fluid px-4 py-4"><div className="alert alert-danger">Nie udało się pobrać danych panelu pracownika.</div></div></div>;
  }

  const sections = [
    ["start", "Start"],
    ["availability", "Dostępność"],
    ["absences", "Urlopy i nieobecności"],
    ["qualifications", "Szkolenia i kwalifikacje"],
    ["documents", "Dokumenty"],
    ["meetings", "Spotkania HR"],
    ["contact", "Kontakt"]
  ];

  return (
    <div id="layoutSidenav_content" className="employee-portal-page">
      <ToastContainer />
      <div className="employee-portal-header shadow-sm">
        <div>
          <div className="employee-portal-eyebrow">Panel pracownika</div>
          <h2>{overview.firstName} {overview.lastName}</h2>
          <div className="employee-portal-subtitle">{overview.departmentName || "Brak przypisanego działu"}{overview.positionNames?.length ? ` · ${overview.positionNames.join(", ")}` : ""}</div>
        </div>
        <div className="employee-portal-header-actions">
          <Button variant="outline-primary" onClick={() => navigate("/app/my-schedule")}>Mój grafik</Button>
          <Button variant="outline-secondary" onClick={() => navigate("/app/employee-profile")}>Moje dane</Button>
        </div>
      </div>

      <div className="container-fluid px-4 py-4">
        <div className="employee-portal-tabs">
          {sections.map(([key, label]) => <button key={key} className={activeSection === key ? "active" : ""} onClick={() => setActiveSection(key)}>{label}</button>)}
        </div>

        {activeSection === "start" && (
          <>
            <div className="employee-portal-kpis">
              <div className="employee-portal-kpi"><span>Wnioski w toku</span><strong>{pendingCount}</strong></div>
              <div className="employee-portal-kpi"><span>Otwarte zmiany 30 dni</span><strong>{openShiftCount}</strong></div>
              <div className="employee-portal-kpi"><span>Zamiany do decyzji</span><strong>{incomingSwapCount}</strong></div>
              <div className="employee-portal-kpi"><span>Nieobecności</span><strong>{overview.absences?.length || 0}</strong></div>
              <div className="employee-portal-kpi"><span>Szkolenia</span><strong>{overview.trainings?.length || 0}</strong></div>
              <div className="employee-portal-kpi"><span>Certyfikaty</span><strong>{overview.certifications?.length || 0}</strong></div>
            </div>

            <div className="employee-portal-grid employee-portal-grid-two">
              <section className="employee-portal-card">
                <div className="employee-portal-card-header"><h5>Moja umowa</h5></div>
                {overview.contract ? (
                  <div className="employee-portal-contract">
                    <div><span>Numer</span><strong>{overview.contract.identifier || "-"}</strong></div>
                    <div><span>Typ</span><strong>{overview.contract.contractType || "-"}</strong></div>
                    <div><span>Okres</span><strong>{formatDate(overview.contract.dateFrom)} – {formatDate(overview.contract.dateTo)}</strong></div>
                    <div><span>Status</span><strong className={overview.contract.active ? (overview.contract.expiringSoon ? "text-warning" : "text-success") : "text-danger"}>{overview.contract.active ? (overview.contract.expiringSoon ? "Wkrótce wygasa" : "Aktywna") : "Nieaktywna"}</strong></div>
                    <div><Button size="sm" variant="outline-primary" onClick={() => setActiveSection("documents")}>Pokaż dokument umowy</Button></div>
                  </div>
                ) : <div className="employee-portal-empty">Brak umowy przypisanej do pracownika.</div>}
              </section>

              <section className="employee-portal-card">
                <div className="employee-portal-card-header"><h5>Szybkie działania</h5></div>
                <div className="employee-portal-actions-grid">
                  <Button onClick={() => setShowDateRequest(true)}>Zgłoś wolne / urlop</Button>
                  <Button variant="outline-primary" onClick={() => setShowAvailabilityRequest(true)}>Zgłoś dostępność</Button>
                  <Button variant="outline-secondary" onClick={() => navigate("/app/my-schedule")}>Grafik, otwarte zmiany i zamiany</Button>
                  <Button variant="outline-secondary" onClick={() => navigate("/app/knowledge-base")}>Baza wiedzy</Button>
                  <Button variant="outline-secondary" onClick={() => navigate("/app/notifications")}>Powiadomienia</Button>
                  <Button variant="outline-secondary" onClick={() => setActiveSection("meetings")}>Umów spotkanie z HR</Button>
                  <Button variant="outline-secondary" onClick={openContactRequest}>Napisz do kierownika</Button>
                </div>
              </section>
            </div>

            <div className="employee-portal-grid employee-portal-grid-two mt-3">
              <section className="employee-portal-card">
                <div className="employee-portal-card-header"><h5>Status pracownika</h5></div>
                <div className="employee-portal-status-row">
                  <span className={`employee-portal-badge ${overview.pupil ? "active" : ""}`}>Uczeń: {overview.pupil ? "TAK" : "NIE"}</span>
                  <span className={`employee-portal-badge ${overview.student ? "active" : ""}`}>Student: {overview.student ? "TAK" : "NIE"}</span>
                  {overview.studentId && <span className="employee-portal-badge">Legitymacja: {overview.studentId}</span>}
                </div>
              </section>

              <section className="employee-portal-card">
                <div className="employee-portal-card-header"><h5>Przełożony / przełożeni</h5></div>
                {overview.supervisors?.length ? (
                  <div className="employee-portal-list">
                    {overview.supervisors.map((supervisor, index) => (
                      <div className="employee-portal-list-item" key={`${supervisor.employeeId}-${supervisor.departmentName || "department"}-${supervisor.role || index}`}>
                        <strong>{supervisor.fullName || "-"}</strong>
                        <span>{supervisor.role || "Przełożony"}{supervisor.departmentName ? ` · ${supervisor.departmentName}` : ""}</span>
                      </div>
                    ))}
                  </div>
                ) : <div className="employee-portal-empty">Brak przypisanego przełożonego.</div>}
              </section>
            </div>
          </>
        )}

        {activeSection === "availability" && (
          <div className="employee-portal-grid employee-portal-grid-two">
            <section className="employee-portal-card">
              <div className="employee-portal-card-header"><h5>Moja zaakceptowana dostępność</h5><Button size="sm" onClick={() => setShowAvailabilityRequest(true)}>Nowa deklaracja</Button></div>
              {availabilities.length === 0 ? <div className="employee-portal-empty">Brak zapisanych niestandardowych okresów dostępności.</div> : (
                <div className="employee-portal-list">
                  {availabilities.map(item => (
                    <div className="employee-portal-list-item" key={item.id}>
                      <div><strong>{availabilityTypeLabels[item.type] || item.type}</strong><span>{formatDate(item.dateFrom)} – {formatDate(item.dateTo)}</span></div>
                      <div className="employee-portal-list-meta">{item.timeFrom && item.timeTo ? `${String(item.timeFrom).slice(0, 5)}–${String(item.timeTo).slice(0, 5)}` : "cały dzień"}{item.recurrence === "WEEKLY" && item.daysOfWeek?.length ? ` · ${formatWeekdays(item.daysOfWeek)}` : ""}</div>
                      {item.comments && <small>{item.comments}</small>}
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section className="employee-portal-card">
              <div className="employee-portal-card-header"><h5>Wnioski o dostępność</h5></div>
              {availabilityRequests.length === 0 ? <div className="employee-portal-empty">Nie masz jeszcze wniosków o dostępność.</div> : (
                <div className="employee-portal-list">
                  {availabilityRequests.map(request => (
                    <div className="employee-portal-list-item" key={request.id}>
                      <div className="d-flex justify-content-between gap-2"><strong>{availabilityTypeLabels[request.availabilityType] || request.availabilityType}</strong><span className={`schedule-request-status schedule-request-status-${String(request.status).toLowerCase()}`}>{requestStatusLabels[request.status]}</span></div>
                      <span>{formatDate(request.dateFrom)} – {formatDate(request.dateTo)}</span>
                      {request.availabilityTimeFrom && request.availabilityTimeTo && <small>{String(request.availabilityTimeFrom).slice(0, 5)}–{String(request.availabilityTimeTo).slice(0, 5)}</small>}
                      {request.managerComment && <small><strong>Odpowiedź:</strong> {request.managerComment}</small>}
                      {["PENDING", "AWAITING_TARGET"].includes(request.status) && <Button size="sm" variant="outline-danger" onClick={() => cancelRequest(request.id)} disabled={saving}>Anuluj</Button>}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {activeSection === "absences" && (
          <div className="employee-portal-grid employee-portal-grid-two">
            <section className="employee-portal-card">
              <div className="employee-portal-card-header"><h5>Moje nieobecności</h5><Button size="sm" onClick={() => setShowDateRequest(true)}>Nowy wniosek</Button></div>
              {overview.absences?.length ? (
                <div className="employee-portal-list">
                  {overview.absences.map(absence => (
                    <div className="employee-portal-list-item" key={absence.id}>
                      <div><strong>{absence.typeName || absence.typeShortName || "Nieobecność"}</strong><span>{formatDate(absence.dateFrom)} – {formatDate(absence.dateTo)}</span></div>
                      {absence.comments && <small>{absence.comments}</small>}
                    </div>
                  ))}
                </div>
              ) : <div className="employee-portal-empty">Brak zarejestrowanych nieobecności.</div>}
            </section>
            <section className="employee-portal-card">
              <div className="employee-portal-card-header"><h5>Wnioski urlopowe i o wolne</h5></div>
              {absenceRequests.length === 0 ? <div className="employee-portal-empty">Brak wniosków.</div> : (
                <div className="employee-portal-list">
                  {absenceRequests.map(request => (
                    <div className="employee-portal-list-item" key={request.id}>
                      <div className="d-flex justify-content-between gap-2"><strong>{request.type === "VACATION" ? "Urlop / nieobecność" : "Prośba o wolne"}</strong><span className={`schedule-request-status schedule-request-status-${String(request.status).toLowerCase()}`}>{requestStatusLabels[request.status]}</span></div>
                      <span>{formatDate(request.dateFrom)} – {formatDate(request.dateTo)}</span>
                      {request.absenceTypeName && <small>{request.absenceTypeName}</small>}
                      {request.managerComment && <small><strong>Odpowiedź:</strong> {request.managerComment}</small>}
                      {["PENDING", "AWAITING_TARGET"].includes(request.status) && <Button size="sm" variant="outline-danger" onClick={() => cancelRequest(request.id)} disabled={saving}>Anuluj</Button>}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {activeSection === "qualifications" && (
          <div className="employee-portal-grid employee-portal-grid-three">
            <section className="employee-portal-card">
              <div className="employee-portal-card-header"><h5>Szkolenia</h5></div>
              {overview.trainings?.length ? <div className="employee-portal-list">{overview.trainings.map(item => <div className="employee-portal-list-item" key={item.id}><strong>{item.trainingName || "Szkolenie"}</strong><span>{item.completed ? "Ukończone" : "W trakcie"}{item.dateFrom ? ` · od ${formatDate(item.dateFrom)}` : ""}</span>{item.evaluation > 0 && <small>Ocena: {item.evaluation}</small>}</div>)}</div> : <div className="employee-portal-empty">Brak szkoleń.</div>}
            </section>
            <section className="employee-portal-card">
              <div className="employee-portal-card-header"><h5>Testy do wykonania</h5></div>
              {availableTests.length ? <div className="employee-portal-list">{availableTests.map(test => <div className="employee-portal-list-item" key={test.id}>
                <div><strong>{test.title}</strong><span>{test.trainingName}{test.departmentName ? ` · ${test.departmentName}` : ""}</span></div>
                <small>{test.questionCount} pytań · próg {test.passingScorePercent}% · podejścia {test.attemptCount}/{test.maxAttempts}</small>
                {test.dueDate && <small className={test.overdue ? "text-danger fw-bold" : "text-muted"}>{test.overdue ? "Termin minął: " : "Termin: "}{formatDate(test.dueDate)}</small>}
                {test.latestAttemptedAt && <small className={test.latestPassed ? "text-success" : "text-danger"}>Ostatni wynik: {test.latestScore}% · {test.latestPassed ? "zaliczony" : "niezaliczony"}</small>}
                <Button size="sm" variant={test.latestPassed ? "outline-primary" : "primary"} onClick={() => openTrainingTest(test)} disabled={test.remainingAttempts <= 0}>{test.remainingAttempts <= 0 ? "Brak kolejnych podejść" : test.attemptCount ? "Wykonaj ponownie" : "Rozpocznij test"}</Button>
              </div>)}</div> : <div className="employee-portal-empty">Brak aktywnych testów przypisanych do Twojego profilu.</div>}
            </section>
            <section className="employee-portal-card">
              <div className="employee-portal-card-header"><h5>Certyfikaty</h5></div>
              {overview.certifications?.length ? <div className="employee-portal-list">{overview.certifications.map(item => <div className="employee-portal-list-item" key={item.id}><strong>{item.certificationType || item.name || "Certyfikat"}</strong><span className={!item.valid ? "text-danger" : item.expiringSoon ? "text-warning" : ""}>{item.noExpirationDate ? "Bezterminowy" : `Ważny do ${formatDate(item.validTo)}`}</span></div>)}</div> : <div className="employee-portal-empty">Brak certyfikatów.</div>}
            </section>
            <section className="employee-portal-card">
              <div className="employee-portal-card-header"><h5>Wyniki testów</h5></div>
              {overview.testAttempts?.length ? <div className="employee-portal-list">{overview.testAttempts.map(item => <div className="employee-portal-list-item" key={item.id}><strong>{item.trainingName || "Test"}</strong><span>{formatDateTime(item.attemptedAt)}</span><small className={item.passed ? "text-success" : "text-danger"}>{item.passed ? "Zaliczony" : "Niezaliczony"}{item.score != null ? ` · wynik ${item.score}` : ""}</small></div>)}</div> : <div className="employee-portal-empty">Brak wyników testów.</div>}
            </section>
          </div>
        )}

        {activeSection === "documents" && (
          <>
            <EmployeeFolderDocumentsCard title="Moje dokumenty pracownicze i umowy" />
            <section className="employee-portal-card mb-3 mt-3">
              <div className="employee-portal-card-header"><h5>Dokumenty i materiały od firmy</h5></div>
              {publishedResources.length === 0 ? (
                <div className="employee-portal-empty">Brak opublikowanych regulaminów i wzorów dokumentów.</div>
              ) : (
                <div className="employee-resource-grid">
                  {publishedResources.map(resource => (
                    <div className="employee-resource-card" key={resource.id}>
                      <div>
                        <span className="employee-resource-category">{resourceCategoryLabels[resource.category] || resource.category}</span>
                        <strong>{resource.title}</strong>
                        {resource.description && <p>{resource.description}</p>}
                        <small>{resource.kind === "FILE" ? `${resource.fileName || "Plik"}${resource.fileSize ? ` · ${formatBytes(resource.fileSize)}` : ""}` : resource.externalUrl}</small>
                      </div>
                      <Button size="sm" variant="outline-primary" onClick={() => openPublishedResource(resource)}>{resource.kind === "LINK" ? "Otwórz" : "Pobierz"}</Button>
                    </div>
                  ))}
                </div>
              )}
            </section>
            <div className="employee-portal-grid employee-portal-grid-two">
              <section className="employee-portal-card">
                <div className="employee-portal-card-header"><h5>Prześlij dokument do HR</h5></div>
                <form onSubmit={uploadDocument}>
                  <Form.Group className="mb-3"><Form.Label>Rodzaj</Form.Label><Form.Select value={documentType} onChange={event => setDocumentType(event.target.value)}><option value="STUDENT_ID">Legitymacja uczniowska / studencka</option><option value="OTHER">Inny dokument dla HR</option></Form.Select></Form.Group>
                  <Form.Group className="mb-3"><Form.Label>Plik PDF, JPG lub PNG</Form.Label><Form.Control type="file" accept="application/pdf,image/jpeg,image/png" onChange={event => setDocumentFile(event.target.files?.[0] || null)} /></Form.Group>
                  <Button type="submit" disabled={saving}>Prześlij</Button>
                </form>
              </section>
              <section className="employee-portal-card">
                <div className="employee-portal-card-header"><h5>Moje przesłane dokumenty</h5></div>
                {documents.length === 0 ? <div className="employee-portal-empty">Nie przesłałeś jeszcze żadnych dokumentów.</div> : <div className="employee-portal-list">{documents.map(document => <div className="employee-portal-list-item" key={document.id}><div><strong>{document.fileName}</strong><span>{document.type === "STUDENT_ID" ? "Legitymacja" : "Inny dokument"} · {formatBytes(document.fileSize)}</span></div><small>{formatDateTime(document.uploadedAt)}</small><div className="d-flex gap-2"><Button size="sm" variant="outline-primary" onClick={() => downloadDocument(document)}>Pobierz</Button><Button size="sm" variant="outline-danger" onClick={() => deleteDocument(document.id)} disabled={saving}>Usuń</Button></div></div>)}</div>}
              </section>
            </div>
          </>
        )}

        {activeSection === "meetings" && (
          <>
            <section className="employee-portal-card employee-portal-group-meetings">
              <div className="employee-portal-card-header"><h5>Zaproszenia na spotkania</h5></div>
              {groupMeetingInvitations.length === 0 ? (
                <div className="employee-portal-empty">Nie masz obecnie żadnych zaproszeń na spotkania grupowe.</div>
              ) : (
                <div className="employee-portal-list">
                  {groupMeetingInvitations.map(invitation => (
                    <div className={`employee-portal-list-item employee-group-meeting ${invitation.scheduleConflict ? "conflict" : ""}`} key={invitation.id}>
                      <div className="d-flex justify-content-between gap-2 flex-wrap">
                        <strong>{invitation.title}</strong>
                        <span className={`employee-appointment-status ${invitation.meetingStatus === "SCHEDULED" ? "booked" : invitation.meetingStatus === "COMPLETED" ? "completed" : "cancelled"}`}>
                          {groupMeetingStatusLabels[invitation.meetingStatus] || invitation.meetingStatus}
                        </span>
                      </div>
                      <span>{formatDateTime(invitation.startAt)} – {invitation.endAt ? new Date(invitation.endAt).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                      {invitation.description && <small>{invitation.description}</small>}
                      {invitation.location && <small>Miejsce: {invitation.location}</small>}
                      {invitation.meetingUrl && invitation.meetingStatus !== "CANCELLED" && <a href={invitation.meetingUrl} target="_blank" rel="noreferrer">Otwórz link do spotkania</a>}
                      {invitation.scheduleConflict && (
                        <div className="employee-group-meeting-conflict">Masz w tym czasie zmianę w grafiku. HR widzi ten konflikt.</div>
                      )}
                      <div className="employee-group-meeting-response">
                        <span>Twoja odpowiedź: <strong>{groupMeetingRsvpLabels[invitation.rsvpStatus] || invitation.rsvpStatus}</strong></span>
                        {invitation.meetingStatus === "SCHEDULED" && new Date(invitation.startAt) > new Date() && (
                          <div className="d-flex flex-wrap gap-2">
                            <Button size="sm" variant={invitation.rsvpStatus === "YES" ? "success" : "outline-success"} onClick={() => respondToGroupMeeting(invitation, "YES")} disabled={saving}>Będę</Button>
                            <Button size="sm" variant={invitation.rsvpStatus === "MAYBE" ? "warning" : "outline-warning"} onClick={() => respondToGroupMeeting(invitation, "MAYBE")} disabled={saving}>Wstępnie tak</Button>
                            <Button size="sm" variant={invitation.rsvpStatus === "NO" ? "danger" : "outline-danger"} onClick={() => respondToGroupMeeting(invitation, "NO")} disabled={saving}>Na pewno nie</Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <div className="employee-portal-grid employee-portal-grid-two">
              <section className="employee-portal-card">
                <div className="employee-portal-card-header"><h5>Wolne terminy HR</h5></div>
                {appointmentSlots.length === 0 ? <div className="employee-portal-empty">HR nie opublikował obecnie wolnych terminów dla Twojego działu.</div> : (
                  <div className="employee-portal-list">
                    {appointmentSlots.map(slot => <div className="employee-portal-list-item" key={slot.id}>
                      <div className="d-flex justify-content-between gap-2"><strong>{slot.title}</strong><span className="employee-appointment-status available">Wolny</span></div>
                      <span>{formatDateTime(slot.startAt)} – {new Date(slot.endAt).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}</span>
                      <small>{slot.location || (slot.meetingUrl ? "Spotkanie online" : "Miejsce zostanie podane przez HR")}</small>
                      {slot.notes && <small>{slot.notes}</small>}
                      <div><Button size="sm" onClick={() => { setSelectedAppointmentSlot(slot); setAppointmentRequest(emptyAppointmentRequest()); }}>Zarezerwuj</Button></div>
                    </div>)}
                  </div>
                )}
              </section>
              <section className="employee-portal-card">
                <div className="employee-portal-card-header"><h5>Moje konsultacje z HR</h5></div>
                {appointments.length === 0 ? <div className="employee-portal-empty">Nie masz jeszcze rezerwacji indywidualnych spotkań z HR.</div> : (
                  <div className="employee-portal-list">
                    {appointments.map(item => <div className="employee-portal-list-item" key={item.id}>
                      <div className="d-flex justify-content-between gap-2"><strong>{item.title || "Spotkanie z HR"}</strong><span className={`employee-appointment-status ${item.status === "BOOKED" ? "booked" : item.status === "COMPLETED" ? "completed" : "cancelled"}`}>{appointmentStatusLabels[item.status] || item.status}</span></div>
                      <span>{formatDateTime(item.startAt)} – {item.endAt ? new Date(item.endAt).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                      <strong className="employee-appointment-subject">{item.subject}</strong>
                      {item.location && <small>Miejsce: {item.location}</small>}
                      {item.meetingUrl && item.status === "BOOKED" && <a href={item.meetingUrl} target="_blank" rel="noreferrer">Otwórz link do spotkania</a>}
                      {item.message && <small>{item.message}</small>}
                      {item.managerNote && <div className="employee-portal-reply"><strong>Informacja HR:</strong> {item.managerNote}</div>}
                      {item.status === "BOOKED" && new Date(item.startAt) > new Date() && <div><Button size="sm" variant="outline-danger" onClick={() => cancelAppointment(item.id)} disabled={saving}>Anuluj rezerwację</Button></div>}
                    </div>)}
                  </div>
                )}
              </section>
            </div>
          </>
        )}

        {activeSection === "contact" && (
          <div className="employee-portal-grid employee-portal-grid-two">
            <section className="employee-portal-card">
              <div className="employee-portal-card-header"><h5>Kontakt z firmą</h5><Button size="sm" onClick={openContactRequest}>Nowe zgłoszenie</Button></div>
              <p className="text-muted mb-3">Tutaj możesz zadać pytanie kierownikowi albo przekazać anonimowe zgłoszenie. Spotkania z HR rezerwujesz w osobnej zakładce z konkretnych wolnych terminów.</p>
              <Button size="sm" variant="outline-primary" onClick={() => setActiveSection("meetings")}>Przejdź do spotkań HR</Button>
            </section>
            <section className="employee-portal-card">
              <div className="employee-portal-card-header"><h5>Moje zgłoszenia</h5></div>
              {contacts.length === 0 ? <div className="employee-portal-empty">Brak zgłoszeń.</div> : <div className="employee-portal-list">{contacts.map(item => <div className="employee-portal-list-item" key={item.id}><div className="d-flex justify-content-between gap-2"><strong>{contactTypeLabels[item.type] || item.type}</strong><span>{contactStatusLabels[item.status] || item.status}</span></div><small>{formatDateTime(item.createdAt)}{item.type === "QUESTION_TO_MANAGER" && item.targetManagerFullName ? ` · Do: ${item.targetManagerFullName}` : ""}</small><span>{item.message}</span>{item.managerReply && <div className="employee-portal-reply"><strong>Odpowiedź:</strong> {item.managerReply}</div>}</div>)}</div>}
            </section>
          </div>
        )}
      </div>

      <Modal show={showDateRequest} onHide={() => setShowDateRequest(false)} centered>
        <Modal.Header closeButton><Modal.Title>Wniosek o wolne / urlop</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3"><Form.Label>Rodzaj</Form.Label><Form.Select value={dateRequest.type} onChange={event => setDateRequest(prev => ({ ...prev, type: event.target.value }))}><option value="DAY_OFF">Prośba o wolne</option><option value="VACATION">Urlop / nieobecność</option></Form.Select></Form.Group>
          <div className="row g-2 mb-3"><div className="col-6"><Form.Label>Od</Form.Label><Form.Control type="date" min={todayString()} value={dateRequest.dateFrom} onChange={event => setDateRequest(prev => ({ ...prev, dateFrom: event.target.value, dateTo: prev.dateTo < event.target.value ? event.target.value : prev.dateTo }))} /></div><div className="col-6"><Form.Label>Do</Form.Label><Form.Control type="date" min={dateRequest.dateFrom} value={dateRequest.dateTo} onChange={event => setDateRequest(prev => ({ ...prev, dateTo: event.target.value }))} /></div></div>
          {dateRequest.type === "VACATION" && <Form.Group className="mb-3"><Form.Label>Typ nieobecności</Form.Label><Form.Select value={dateRequest.absenceTypeId} onChange={event => setDateRequest(prev => ({ ...prev, absenceTypeId: event.target.value }))}><option value="">-- wybierz --</option>{absenceTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}</Form.Select></Form.Group>}
          <Form.Group><Form.Label>Uwagi</Form.Label><Form.Control as="textarea" rows={3} value={dateRequest.employeeComment} onChange={event => setDateRequest(prev => ({ ...prev, employeeComment: event.target.value }))} /></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowDateRequest(false)}>Anuluj</Button><Button onClick={submitDateRequest} disabled={saving}>Wyślij wniosek</Button></Modal.Footer>
      </Modal>

      <Modal show={showAvailabilityRequest} onHide={() => setShowAvailabilityRequest(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Deklaracja dostępności</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="alert alert-info">Tutaj zgłaszasz dodatkową gotowość do pracy lub preferencję. Niedostępność zgłaszaj jako wolne albo urlop. Deklaracja zacznie wpływać na grafik dopiero po zatwierdzeniu przez kierownika.</div>
          <Form.Group className="mb-3"><Form.Label>Rodzaj</Form.Label><Form.Select value={availabilityRequest.availabilityType} onChange={event => setAvailabilityRequest(prev => ({ ...prev, availabilityType: event.target.value }))}><option value="AVAILABLE">Mogę pracować</option><option value="PREFERRED">Preferuję pracę</option></Form.Select></Form.Group>
          <div className="row g-2 mb-3"><div className="col-md-3"><Form.Label>Od</Form.Label><Form.Control type="date" min={todayString()} value={availabilityRequest.dateFrom} onChange={event => setAvailabilityRequest(prev => ({ ...prev, dateFrom: event.target.value, dateTo: prev.dateTo < event.target.value ? event.target.value : prev.dateTo }))} /></div><div className="col-md-3"><Form.Label>Do</Form.Label><Form.Control type="date" min={availabilityRequest.dateFrom} value={availabilityRequest.dateTo} onChange={event => setAvailabilityRequest(prev => ({ ...prev, dateTo: event.target.value }))} /></div><div className="col-md-3"><Form.Label>Godzina od</Form.Label><Form.Control type="time" value={availabilityRequest.availabilityTimeFrom} onChange={event => setAvailabilityRequest(prev => ({ ...prev, availabilityTimeFrom: event.target.value }))} /></div><div className="col-md-3"><Form.Label>Godzina do</Form.Label><Form.Control type="time" value={availabilityRequest.availabilityTimeTo} onChange={event => setAvailabilityRequest(prev => ({ ...prev, availabilityTimeTo: event.target.value }))} /></div></div>
          <Form.Group className="mb-3"><Form.Label>Powtarzanie</Form.Label><Form.Select value={availabilityRequest.availabilityRecurrence} onChange={event => setAvailabilityRequest(prev => ({ ...prev, availabilityRecurrence: event.target.value, availabilityDaysOfWeek: event.target.value === "WEEKLY" ? prev.availabilityDaysOfWeek : [] }))}><option value="NONE">Każdy dzień okresu</option><option value="WEEKLY">Wybrane dni tygodnia</option></Form.Select></Form.Group>
          {availabilityRequest.availabilityRecurrence === "WEEKLY" && <div className="employee-portal-weekdays mb-3">{weekdayOptions.map(([day, label]) => <label key={day} className={availabilityRequest.availabilityDaysOfWeek.includes(day) ? "selected" : ""}><Form.Check type="checkbox" checked={availabilityRequest.availabilityDaysOfWeek.includes(day)} onChange={() => toggleWeekday(day)} /><span>{label}</span></label>)}</div>}
          <Form.Group><Form.Label>Uwagi</Form.Label><Form.Control as="textarea" rows={3} value={availabilityRequest.employeeComment} onChange={event => setAvailabilityRequest(prev => ({ ...prev, employeeComment: event.target.value }))} /></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowAvailabilityRequest(false)}>Anuluj</Button><Button onClick={submitAvailabilityRequest} disabled={saving}>Wyślij do zatwierdzenia</Button></Modal.Footer>
      </Modal>

      <Modal show={showTestModal} onHide={() => !submittingTest && setShowTestModal(false)} centered size="lg" backdrop="static">
        <Modal.Header closeButton={!submittingTest}><Modal.Title>{activeTest?.title || "Test szkoleniowy"}</Modal.Title></Modal.Header>
        <Modal.Body>
          {activeTest && <>
            <div className="mb-3">
              <strong>{activeTest.trainingName}{activeTest.departmentName ? ` · ${activeTest.departmentName}` : ""}</strong>
              <div className="text-muted">Próg zaliczenia: {activeTest.passingScorePercent}% · pozostało podejść: {activeTest.remainingAttempts}/{activeTest.maxAttempts}</div>
              {activeTest.dueDate && <div className={activeTest.overdue ? "text-danger fw-bold" : "text-muted"}>{activeTest.overdue ? "Termin wykonania minął: " : "Termin wykonania: "}{formatDate(activeTest.dueDate)}</div>}
            </div>
            {activeTest.instructions && <div className="alert alert-info">{activeTest.instructions}</div>}
            <div className="d-grid gap-3">
              {(activeTest.questions || []).map((question, questionIndex) => <div className="employee-portal-test-question" key={question.id}>
                <strong>{questionIndex + 1}. {question.text}</strong>
                {question.multipleAnswers && <small className="text-muted">Możliwy jest wybór kilku odpowiedzi.</small>}
                <div className="d-grid gap-2 mt-2">
                  {(question.answers || []).map(answer => <label className="employee-portal-test-answer" key={answer.id}>
                    <Form.Check type={question.multipleAnswers ? "checkbox" : "radio"} name={`test-question-${question.id}`} checked={(testAnswers[question.id] || []).includes(answer.id)} onChange={() => toggleTestAnswer(question, answer.id)} />
                    <span>{answer.text}</span>
                  </label>)}
                </div>
              </div>)}
            </div>
          </>}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowTestModal(false)} disabled={submittingTest}>Anuluj</Button><Button onClick={submitTrainingTest} disabled={submittingTest || (activeTest?.remainingAttempts ?? 1) <= 0}>{submittingTest ? "Sprawdzanie..." : "Zakończ i sprawdź"}</Button></Modal.Footer>
      </Modal>

      <Modal show={!!selectedAppointmentSlot} onHide={() => !saving && setSelectedAppointmentSlot(null)} centered>
        <Modal.Header closeButton={!saving}><Modal.Title>Rezerwacja spotkania z HR</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedAppointmentSlot && <div className="alert alert-info"><strong>{selectedAppointmentSlot.title}</strong><br />{formatDateTime(selectedAppointmentSlot.startAt)} – {new Date(selectedAppointmentSlot.endAt).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}{selectedAppointmentSlot.location ? <><br />{selectedAppointmentSlot.location}</> : null}</div>}
          <Form.Group className="mb-3"><Form.Label>Temat spotkania</Form.Label><Form.Control value={appointmentRequest.subject} onChange={event => setAppointmentRequest(prev => ({ ...prev, subject: event.target.value }))} placeholder="np. pytanie dotyczące umowy" /></Form.Group>
          <Form.Group><Form.Label>Dodatkowa informacja dla HR</Form.Label><Form.Control as="textarea" rows={4} value={appointmentRequest.message} onChange={event => setAppointmentRequest(prev => ({ ...prev, message: event.target.value }))} /></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setSelectedAppointmentSlot(null)} disabled={saving}>Anuluj</Button><Button onClick={bookAppointment} disabled={saving}>Zarezerwuj termin</Button></Modal.Footer>
      </Modal>

      <Modal show={showContactRequest} onHide={() => setShowContactRequest(false)} centered>
        <Modal.Header closeButton><Modal.Title>Nowe zgłoszenie</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3"><Form.Label>Rodzaj</Form.Label><Form.Select value={contactRequest.type} onChange={event => { const type = event.target.value; setContactRequest(prev => ({ ...prev, type, targetManagerEmployeeId: type === "QUESTION_TO_MANAGER" ? (supervisorOptions.length === 1 ? String(supervisorOptions[0].employeeId) : prev.targetManagerEmployeeId) : "" })); }}><option value="QUESTION_TO_MANAGER">Pytanie do kierownika</option><option value="ANONYMOUS_REPORT">Anonimowe zgłoszenie</option></Form.Select></Form.Group>
          {contactRequest.type === "QUESTION_TO_MANAGER" && (supervisorOptions.length ? <Form.Group className="mb-3"><Form.Label>Kierownik</Form.Label><Form.Select value={contactRequest.targetManagerEmployeeId} onChange={event => setContactRequest(prev => ({ ...prev, targetManagerEmployeeId: event.target.value }))}><option value="">-- wybierz kierownika --</option>{supervisorOptions.map(supervisor => <option key={supervisor.employeeId} value={supervisor.employeeId}>{supervisor.fullName}{supervisor.role ? ` · ${supervisor.role}` : ""}{supervisor.departmentName ? ` · ${supervisor.departmentName}` : ""}</option>)}</Form.Select></Form.Group> : <div className="alert alert-warning">Nie masz przypisanego kierownika. Skontaktuj się z HR lub administratorem, aby uzupełnić przełożonego w dziale.</div>)}
          {contactRequest.type === "ANONYMOUS_REPORT" && <div className="alert alert-secondary">Kierownik zobaczy zgłoszenie jako anonimowe. System zachowuje techniczne powiązanie z kontem wyłącznie po stronie bazy danych.</div>}
          <Form.Group><Form.Label>Treść</Form.Label><Form.Control as="textarea" rows={5} value={contactRequest.message} onChange={event => setContactRequest(prev => ({ ...prev, message: event.target.value }))} /></Form.Group>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowContactRequest(false)}>Anuluj</Button><Button onClick={submitContactRequest} disabled={saving || (contactRequest.type === "QUESTION_TO_MANAGER" && !contactRequest.targetManagerEmployeeId)}>Wyślij</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmployeePortal;
